import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Legal Entities — the "Companies" list MD-08 field 4 picks from ("which
 * company in the group owns this place"), also named by the audit line's
 * company field and the invite box's Company pick.
 *
 * Deliberately minimal, because the spec is: no MD screen, no BR rules, and
 * one stated behaviour — entity difference is what makes a stock transfer
 * INTERCOMPANY rather than internal (a classification; no posting rule
 * exists anywhere in the document). One entity is seeded per tenant, named
 * after the company, so a single-entity tenant never meets the field
 * (PART 16's "hidden and set silently when there is only one").
 *
 * Permission area: `inventory` — the warehouses module's own (MD-08 lives
 * under Inventory & Warehouses).
 */
// ── The fixed sentences (MD-RULE-4's shape) ─────────────────────────────────
export const LEGAL_ENTITY_MESSAGES = {
    nameRequired: 'Type the company’s name.',
    nameTaken: (name) => `There is already a company called ${name}.`,
    /** MD-RULE-4: name the count, offer the alternative. */
    inUse: (name, count) => `${name} owns ${count} ${count === 1 ? 'warehouse' : 'warehouses'} and cannot be deleted. Switch it off instead.`,
    inactive: (name) => `${name} is switched off and cannot be picked.`,
    lastEntity: 'A company must keep at least one legal entity.',
};
/**
 * D46 — the legal-entity family's CONVERTED parameterised refusals, structured.
 * Each builder composes its `LEGAL_ENTITY_MESSAGES` twin's sentence plus an ICU
 * key and params; sentence parity is pinned by `refusal-census.spec.ts`. The
 * `inUse` count is a genuine warehouse count (raw; noun-only warehouse/
 * warehouses plural); the other two carry a display name only.
 */
export const LEGAL_ENTITY_REFUSALS = {
    nameTaken: (name) => ({
        message: LEGAL_ENTITY_MESSAGES.nameTaken(name),
        key: 'entities.errNameTaken',
        params: { name },
    }),
    inUse: (name, count) => ({
        message: LEGAL_ENTITY_MESSAGES.inUse(name, count),
        key: 'entities.errInUse',
        params: { name, count },
    }),
    inactive: (name) => ({
        message: LEGAL_ENTITY_MESSAGES.inactive(name),
        key: 'entities.errInactive',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const LegalEntitySchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    isActive: z.boolean(),
    /** The delete pre-check — warehouses this entity owns. */
    warehousesOwned: z.number().int(),
    createdAt: z.string(),
})
    .strict();
export const CreateLegalEntityRequestSchema = z
    .object({
    name: z.string().trim().min(1, LEGAL_ENTITY_MESSAGES.nameRequired).max(120),
})
    .strict();
export const UpdateLegalEntityRequestSchema = z
    .object({
    name: z.string().trim().min(1, LEGAL_ENTITY_MESSAGES.nameRequired).max(120).optional(),
})
    .strict();
const entityParams = z.object({ entityId: z.uuid() }).strict();
export const legalEntitiesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/legal-entities',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ entities: LegalEntitySchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Companies list (inventory.view) — usually one row; unpaged like every vocabulary.',
    },
    create: {
        method: 'POST',
        path: '/api/legal-entities',
        body: CreateLegalEntityRequestSchema,
        responses: {
            201: LegalEntitySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'A second (or third) company in the group (inventory.create).',
    },
    update: {
        method: 'PATCH',
        path: '/api/legal-entities/:entityId',
        pathParams: entityParams,
        body: UpdateLegalEntityRequestSchema,
        responses: {
            200: LegalEntitySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Rename (inventory.edit) — renames it on every warehouse at once; the reference is the id.',
    },
    deactivate: {
        method: 'POST',
        path: '/api/legal-entities/:entityId/deactivate',
        pathParams: entityParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: LegalEntitySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch off (inventory.edit) — not pickable on a NEW warehouse; owned places keep their owner. The last active entity refuses (a company must keep one).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/legal-entities/:entityId/reactivate',
        pathParams: entityParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: LegalEntitySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (inventory.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/legal-entities/:entityId',
        pathParams: entityParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — refused while it owns any warehouse (MD-RULE-4’s counted sentence) or is the last entity; the RESTRICT FK refuses even a bug here.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=legal-entities.js.map